import React, { useState } from 'react';
import { X, Mail, Tag, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USInsureVerifyModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await axios.post(`${API}/usinsure/verify-email`, { email });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setResult(null);
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="usinsure-modal">
      <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#D4AF37] to-[#B5952F] px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Tag className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">US Insurance Discount</h2>
                <p className="text-white/80 text-sm">Exclusive 10% off for clients</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {result ? (
            // Success State
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Your Code is Ready!</h3>
              <p className="text-gray-600 mb-6">Use this code at checkout to get 10% off your order:</p>
              
              <div className="bg-gray-100 rounded-lg p-4 mb-6">
                <p className="text-2xl font-bold text-[#D4AF37] tracking-wider" data-testid="promo-code-result">
                  {result.code}
                </p>
                <p className="text-xs text-gray-500 mt-2">Single-use code • Valid for one purchase</p>
              </div>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(result.code);
                }}
                className="w-full bg-[#1A1A1A] text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors mb-3"
              >
                Copy Code
              </button>
              <button
                onClick={handleClose}
                className="w-full border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          ) : (
            // Form State
            <>
              <div className="text-center mb-6">
                <p className="text-gray-600">
                  Enter your email address to verify your eligibility and receive your exclusive discount code.
                </p>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-700 text-sm font-medium">Not Eligible</p>
                    <p className="text-red-600 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="Enter your email"
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                      data-testid="usinsure-email-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-[#D4AF37] text-white py-3 rounded-lg font-bold uppercase tracking-wider hover:bg-[#B5952F] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="usinsure-verify-button"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Get My 10% Off Code'
                  )}
                </button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-4">
                This offer is exclusively for verified US Insurance clients.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default USInsureVerifyModal;
